"use client";

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { t, type Locale } from "@/lib/i18n";

function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

export type DateRangeValue = { from: string; to: string };

function pad(n: number) {
  return String(n).padStart(2, "0");
}

export function toIsoDate(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function formatDateDisplay(iso?: string | null) {
  if (!iso) return "";
  return iso.replaceAll("-", "/");
}

function fromIso(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number) {
  const next = startOfDay(d);
  next.setDate(next.getDate() + n);
  return next;
}

function startOfWeek(d: Date) {
  const next = startOfDay(d);
  next.setDate(next.getDate() - next.getDay());
  return next;
}

function quarterStart(d: Date) {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}

function readLocale(explicit?: Locale): Locale {
  if (explicit) return explicit;
  if (typeof window === "undefined") return "zh-Hant";
  const stored = window.localStorage.getItem("kairos.locale");
  if (stored === "zh-Hans" || stored === "en" || stored === "zh-Hant") return stored;
  return "zh-Hant";
}

function weekdays(locale: Locale) {
  const loc = locale === "en" ? "en" : locale === "zh-Hans" ? "zh-CN" : "zh-TW";
  return Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(loc, { weekday: "short" }).format(new Date(2026, 7, 23 + i)),
  );
}

function monthLabel(year: number, month: number, locale: Locale) {
  if (locale === "en") {
    return new Intl.DateTimeFormat("en", { month: "long" }).format(new Date(year, month, 1));
  }
  return ["一月", "二月", "三月", "四月", "五月", "六月", "七月", "八月", "九月", "十月", "十一月", "十二月"][month];
}

function monthCells(year: number, month: number) {
  const first = new Date(year, month, 1);
  const start = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

function CalendarIcon({ className = "field-icon" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15.5" rx="2.2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 3.5v3.2M16 3.5v3.2M3.5 9.5h17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <rect x="13.2" y="13.2" width="4.2" height="4.2" rx="0.7" fill="currentColor" />
    </svg>
  );
}

function Chevron({ dir }: { dir: "l" | "r" }) {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d={dir === "l" ? "M7.4 2.4L3.6 6l3.8 3.6" : "M4.6 2.4L8.4 6 4.6 9.6"}
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function captionMark(text: string, required?: boolean) {
  if (!required || text.trim().endsWith("*")) return text;
  return `${text} *`;
}

const PRESET_KEYS = [
  ["dateToday", "dateYesterday"],
  ["dateThisWeek", "dateLastWeek"],
  ["dateThisMonth", "dateLastMonth"],
  ["dateThisQuarter", "dateLastQuarter"],
  ["dateLast7Days", "dateLast14Days"],
] as const;

const ALL_HISTORY = "dateAllHistory";

function presetRange(key: string, today: Date): DateRangeValue {
  if (key === "dateToday") return { from: toIsoDate(today), to: toIsoDate(today) };
  if (key === "dateYesterday") {
    const y = addDays(today, -1);
    return { from: toIsoDate(y), to: toIsoDate(y) };
  }
  if (key === "dateThisWeek") return { from: toIsoDate(startOfWeek(today)), to: toIsoDate(today) };
  if (key === "dateLastWeek") {
    const end = addDays(startOfWeek(today), -1);
    return { from: toIsoDate(startOfWeek(end)), to: toIsoDate(end) };
  }
  if (key === "dateThisMonth") return { from: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: toIsoDate(today) };
  if (key === "dateLastMonth") {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const to = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: toIsoDate(from), to: toIsoDate(to) };
  }
  if (key === "dateThisQuarter") return { from: toIsoDate(quarterStart(today)), to: toIsoDate(today) };
  if (key === "dateLastQuarter") {
    const from = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 - 3, 1);
    const to = new Date(from.getFullYear(), from.getMonth() + 3, 0);
    return { from: toIsoDate(from), to: toIsoDate(to) };
  }
  if (key === "dateLast7Days") return { from: toIsoDate(addDays(today, -6)), to: toIsoDate(today) };
  return { from: toIsoDate(addDays(today, -13)), to: toIsoDate(today) };
}

function placePopover(trigger: HTMLElement, width: number): CSSProperties {
  const rect = trigger.getBoundingClientRect();
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const top = rect.bottom + 8;
  const openUp = window.innerHeight - rect.bottom < 340 && rect.top > 340;
  return {
    position: "fixed",
    left,
    width,
    top: openUp ? undefined : top,
    bottom: openUp ? window.innerHeight - rect.top + 8 : undefined,
    zIndex: 110,
  };
}

function CalendarView({
  locale,
  year,
  month,
  onYear,
  onMonth,
  mode,
  picked,
  draftFrom,
  draftTo,
  onPick,
}: {
  locale: Locale;
  year: number;
  month: number;
  onYear: (year: number) => void;
  onMonth: (month: number) => void;
  mode: "single" | "range";
  picked?: string | null;
  draftFrom?: string | null;
  draftTo?: string | null;
  onPick: (iso: string) => void;
}) {
  const days = weekdays(locale);
  const cells = monthCells(year, month);
  const from = draftFrom || "";
  const to = draftTo || "";

  return (
    <div className="cal-panel">
      <div className="cal-nav">
        <div className="cal-nav-group">
          <button type="button" className="cal-nav-btn" onClick={() => (month === 0 ? (onMonth(11), onYear(year - 1)) : onMonth(month - 1))}>
            <Chevron dir="l" />
          </button>
          <span className="cal-nav-label">{monthLabel(year, month, locale)}</span>
          <button type="button" className="cal-nav-btn" onClick={() => (month === 11 ? (onMonth(0), onYear(year + 1)) : onMonth(month + 1))}>
            <Chevron dir="r" />
          </button>
        </div>
        <div className="cal-nav-group">
          <button type="button" className="cal-nav-btn" onClick={() => onYear(year - 1)}>
            <Chevron dir="l" />
          </button>
          <span className="cal-nav-label">{year}</span>
          <button type="button" className="cal-nav-btn" onClick={() => onYear(year + 1)}>
            <Chevron dir="r" />
          </button>
        </div>
      </div>
      <div className="cal-week">
        {days.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="cal-grid">
        {cells.map((day) => {
          const iso = toIsoDate(day);
          const outside = day.getMonth() !== month;
          const isPicked = mode === "single" && iso === picked;
          const isStart = mode === "range" && iso === from;
          const isEnd = mode === "range" && iso === to;
          const inRange = mode === "range" && from && to && iso > from && iso < to;
          return (
            <button
              key={iso}
              type="button"
              disabled={outside}
              className={cn(
                "cal-cell",
                outside && "cal-cell--out",
                isPicked && "cal-cell--picked",
                inRange && "cal-cell--in",
                isStart && "cal-cell--start",
                isEnd && "cal-cell--end",
              )}
              onClick={() => onPick(iso)}
            >
              <span className="cal-num">{day.getDate()}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DatePicker({
  label,
  value,
  onChange,
  required,
  disabled,
  className,
  locale,
  clearable,
}: {
  label: string;
  value?: string | null;
  onChange: (value: string | null) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  locale?: Locale;
  /** Show in-field × to clear. Defaults to true when the field is not required. */
  clearable?: boolean;
}) {
  const loc = readLocale(locale);
  const filled = Boolean(value);
  const canClear = clearable ?? !required;
  const [open, setOpen] = useState(false);
  const seed = value ? fromIso(value) : new Date();
  const [year, setYear] = useState(seed.getFullYear());
  const [month, setMonth] = useState(seed.getMonth());
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const place = () => setMenuStyle(placePopover(triggerRef.current!, 320));
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const node = event.target as Node;
      if (rootRef.current?.contains(node) || popRef.current?.contains(node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pop =
    open && typeof document !== "undefined"
      ? createPortal(
          <div ref={popRef} className="cal-pop" style={menuStyle}>
            <CalendarView
              locale={loc}
              year={year}
              month={month}
              onYear={setYear}
              onMonth={setMonth}
              mode="single"
              picked={value}
              onPick={(iso) => {
                onChange(iso);
                setOpen(false);
              }}
            />
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cn("dd dd--field", className)}>
      <div className="field-wrap">
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          data-open={open || undefined}
          className={cn(
            "field field--icon field-labeled",
            (filled || open) && "field--filled",
            canClear && filled && "field--clearable",
          )}
          onClick={() => {
            if (disabled) return;
            const next = !open;
            if (next) {
              const d = value ? fromIso(value) : new Date();
              setYear(d.getFullYear());
              setMonth(d.getMonth());
            }
            setOpen(next);
          }}
        >
          <span className="field-caption">{captionMark(label, required)}</span>
          {filled || open ? <span className="field-value">{formatDateDisplay(value)}</span> : null}
          <CalendarIcon />
        </button>
        {canClear && filled ? (
          <button
            type="button"
            className="field-clear"
            aria-label="clear"
            disabled={disabled}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onChange(null);
              setOpen(false);
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      {pop}
    </div>
  );
}

export function DateRangePicker({
  label,
  value,
  onChange,
  required,
  disabled,
  className,
  locale,
  variant = "pill",
  allowAllHistory = false,
}: {
  label: string;
  value?: DateRangeValue | null;
  onChange: (value: DateRangeValue | null) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  locale?: Locale;
  variant?: "field" | "pill" | "icon";
  /** When true, presets include 全部歷史 and confirm may clear the range. */
  allowAllHistory?: boolean;
}) {
  const loc = readLocale(locale);
  const filled = Boolean(value?.from && value?.to);
  const allHistory = allowAllHistory && !filled;
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(value?.from || "");
  const [draftTo, setDraftTo] = useState(value?.to || "");
  const [preset, setPreset] = useState(allHistory ? ALL_HISTORY : "");
  const seed = value?.from ? fromIso(value.from) : new Date();
  const [year, setYear] = useState(seed.getFullYear());
  const [month, setMonth] = useState(seed.getMonth());
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const place = () => setMenuStyle(placePopover(triggerRef.current!, 560));
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const node = event.target as Node;
      if (rootRef.current?.contains(node) || popRef.current?.contains(node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function openMenu() {
    if (disabled) return;
    const nextAll = allowAllHistory && !(value?.from && value?.to);
    setDraftFrom(value?.from || "");
    setDraftTo(value?.to || "");
    setPreset(nextAll ? ALL_HISTORY : "");
    const d = value?.from ? fromIso(value.from) : new Date();
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setOpen(true);
  }

  function pickDay(iso: string) {
    setPreset("");
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(iso);
      setDraftTo("");
      return;
    }
    if (iso < draftFrom) {
      setDraftTo(draftFrom);
      setDraftFrom(iso);
      return;
    }
    setDraftTo(iso);
  }

  const rangeText =
    filled && value ? `${formatDateDisplay(value.from)} - ${formatDateDisplay(value.to)}` : "";
  const display = allHistory ? t(loc, ALL_HISTORY) : rangeText;
  const draftDisplay =
    preset === ALL_HISTORY || (!draftFrom && !draftTo && allowAllHistory)
      ? t(loc, ALL_HISTORY)
      : draftFrom && draftTo
        ? `${formatDateDisplay(draftFrom)} - ${formatDateDisplay(draftTo)}`
        : draftFrom
          ? formatDateDisplay(draftFrom)
          : "";

  const pop =
    open && typeof document !== "undefined"
      ? createPortal(
          <div ref={popRef} className="cal-pop" style={menuStyle}>
            <div className="cal-presets">
              {allowAllHistory ? (
                <button
                  type="button"
                  className="cal-preset cal-preset--wide"
                  data-active={preset === ALL_HISTORY || undefined}
                  onClick={() => {
                    setPreset(ALL_HISTORY);
                    setDraftFrom("");
                    setDraftTo("");
                  }}
                >
                  {t(loc, ALL_HISTORY)}
                </button>
              ) : null}
              {PRESET_KEYS.flat().map((key) => (
                <button
                  key={key}
                  type="button"
                  className="cal-preset"
                  data-active={preset === key || undefined}
                  onClick={() => {
                    const next = presetRange(key, startOfDay(new Date()));
                    setPreset(key);
                    setDraftFrom(next.from);
                    setDraftTo(next.to);
                    const d = fromIso(next.from);
                    setYear(d.getFullYear());
                    setMonth(d.getMonth());
                  }}
                >
                  {t(loc, key)}
                </button>
              ))}
            </div>
            <div>
              {variant === "icon" ? (
                <div className="cal-pop-head field field--pill field--icon field-inline field--filled">
                  <span className="field-caption">{captionMark(label, required)}</span>
                  <span className="field-split" />
                  <span className="field-value">{draftDisplay || t(loc, ALL_HISTORY)}</span>
                  <CalendarIcon />
                </div>
              ) : null}
              <CalendarView
                locale={loc}
                year={year}
                month={month}
                onYear={setYear}
                onMonth={setMonth}
                mode="range"
                draftFrom={draftFrom}
                draftTo={draftTo || draftFrom}
                onPick={pickDay}
              />
              <div className="cal-foot">
                <button type="button" className="cal-foot-btn" onClick={() => setOpen(false)}>
                  {t(loc, "dateCancel")}
                </button>
                <button
                  type="button"
                  className="cal-foot-btn cal-foot-btn--ok"
                  onClick={() => {
                    if (preset === ALL_HISTORY || (allowAllHistory && !draftFrom && !draftTo)) {
                      onChange(null);
                      setOpen(false);
                      return;
                    }
                    if (draftFrom && draftTo) onChange({ from: draftFrom, to: draftTo });
                    setOpen(false);
                  }}
                >
                  {t(loc, "dateConfirm")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cn("dd", variant === "field" && "dd--field", className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        data-open={open || undefined}
        aria-label={label}
        className={
          variant === "icon"
            ? cn("tf-tool", (filled || open) && "tf-tool--lit")
            : cn(
                "field field--icon",
                variant === "pill" ? "field--pill field-inline" : "field-labeled",
                (filled || open || allHistory) && "field--filled",
              )
        }
        onClick={() => (open ? setOpen(false) : openMenu())}
      >
        {variant === "icon" ? (
          <CalendarIcon className="tf-tool-icon" />
        ) : variant === "pill" ? (
          <>
            <span className="field-caption">{captionMark(label, required)}</span>
            {display ? (
              <>
                <span className="field-split" />
                <span className="field-value">{display}</span>
              </>
            ) : null}
          </>
        ) : (
          <>
            <span className="field-caption">{captionMark(label, required)}</span>
            {filled || open || allHistory ? <span className="field-value">{display}</span> : null}
          </>
        )}
        {variant === "icon" ? null : <CalendarIcon />}
      </button>
      {pop}
    </div>
  );
}
